import _Close from "@mui/icons-material/Close";
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useCallback, useMemo } from 'react';
import { Dialog, IconButton, Slide } from '@mui/material';
import styled from 'styled-components';
import { FONTS, SIZES, Typography } from '@fupa/fupa-uikit';
import { styled as muiStyled } from '@mui/material/styles';
import { SoftTickerState } from 'app/reducers/softTickerReducer';
import UpdateResultSuccess from 'app/components/match/liveResult/UpdateSuccess';
import { closeSoftTicker, loadSoftTickerData, openAddScorer } from 'app/actions/softTickerActions';
import { useSoftTickerContext } from 'app/hooks/useSoftTickerContext';
import UpdateResult from 'app/components/match/liveResult/UpdateResult';
import UpdateScorer from 'app/components/match/liveResult/updateScorer/UpdateScorer';
import { sendEventToGA4 } from 'app/helpers/gtagHelpers';
import useAllowSoftTicker, { useAllowSoftTickerEvents } from 'app/hooks/useAllowSoftTicker';
import { useStoreSofttickerDataInSessionStorage } from 'app/hooks/useStoreSofttickerDataInSessionStorage';
import useNativeReloadSoftticker from 'app/hooks/useNativeReloadSoftticker';
import { useMarkOpenDialog } from 'app/hooks/useMarkOpenDialog';
import { DialogName } from 'app/common/DialogName';
const Header = styled.div.withConfig({
  componentId: "sc-8ixzlp-0"
})(["display:grid;grid-template-columns:auto 1fr;align-content:center;"]);
const Body = styled.div.withConfig({
  componentId: "sc-8ixzlp-1"
})(["padding:", " ", " ", ";display:flex;flex-direction:column;gap:", ";@media only screen and (max-width:340px){padding:", " 0 ", ";}"], SIZES['32'], SIZES['16'], SIZES['16'], SIZES['16'], SIZES['24'], SIZES['8']);
const Title = styled(Typography).withConfig({
  componentId: "sc-8ixzlp-2"
})(["&&&{font-size:", ";align-self:center;}"], FONTS['18']);
const Transition = /*#__PURE__*/React.forwardRef(function Transition(props, ref) {
  return /*#__PURE__*/React.createElement(Slide, _extends({
    direction: "up",
    ref: ref
  }, props, {
    children: props.children
  }));
});
const CustomDialog = muiStyled(Dialog, {
  shouldForwardProp: prop => prop !== 'height'
})(_ref => {
  let {
    height,
    theme
  } = _ref;
  return {
    '& .MuiDialog-container': {
      [theme.breakpoints.down('sm')]: {
        alignItems: 'flex-end'
      }
    },
    '& .MuiPaper-root': {
      margin: 0,
      padding: SIZES['8'],
      width: '100%',
      height: height ? "".concat(height, "px") : 'auto',
      transition: 'height 0.1s',
      maxHeight: '100%'
    }
  };
});
const dialogTitleByState = {
  [SoftTickerState.Result]: 'Live-Spielstand aktualisieren',
  [SoftTickerState.ResultSuccess]: 'Live-Spielstand aktualisieren',
  [SoftTickerState.ScorerAdd]: 'Torschütze hinzufügen',
  [SoftTickerState.ScorerUpdate]: 'Torschütze bearbeiten'
};
const closeEventByState = {
  [SoftTickerState.Result]: () => ({
    event: 'live_result'
  }),
  [SoftTickerState.ResultSuccess]: () => ({
    event: 'live_result',
    view: 'layer_report_result_success'
  }),
  [SoftTickerState.ScorerAdd]: view => ({
    event: 'scorer',
    view: view !== null && view !== void 0 ? view : undefined
  })
};
const SoftTickerDialog = _ref2 => {
  let {
    match,
    tickerEvents,
    onMatchChange,
    onScorerChange,
    onScorerDelete
  } = _ref2;
  const {
    softTickerData,
    dispatch
  } = useSoftTickerContext();
  const initializeSoftTickerData = useCallback(data => dispatch(loadSoftTickerData(data)), [dispatch]);
  useMarkOpenDialog(softTickerData.open, DialogName.Softticker);
  useStoreSofttickerDataInSessionStorage(softTickerData, initializeSoftTickerData);
  useNativeReloadSoftticker(softTickerData.open);
  const closeDialog = () => {
    dispatch(closeSoftTicker());
  };
  const handleUpdateScorer = (matchEvent, isUpdate) => {
    closeDialog();
    onScorerChange(matchEvent, isUpdate);
  };
  const handleDeleteScorer = matchEventId => {
    closeDialog();
    onScorerDelete(matchEventId);
  };
  const handleResultResumeToScorer = () => {
    dispatch(openAddScorer('layer_report_scorer'));
  };
  const handleClose = (event, view) => {
    if (event) {
      const eventData = {
        live_result: {
          name: 'report_live_result',
          ui_view: 'layer_report_live_result'
        },
        scorer: {
          name: 'report_scorer',
          ui_view: 'layer_report_scorer'
        }
      };
      sendEventToGA4("soft_ticker_".concat(eventData[event].name), {
        soft_ticker_action: 'cancel',
        ui_view: view !== null && view !== void 0 ? view : eventData[event].ui_view,
        match_slug: match.slug
      });
    }
    closeDialog();
  };
  const handleScorerClose = () => {
    var _softTickerData$initi;
    const view = (_softTickerData$initi = softTickerData.initiatorView) !== null && _softTickerData$initi !== void 0 ? _softTickerData$initi : undefined;
    handleClose('scorer', view);
  };
  const renderChild = {
    [SoftTickerState.Result]: /*#__PURE__*/React.createElement(UpdateResult, {
      match: match,
      onClose: () => handleClose('live_result'),
      onResultChange: onMatchChange
    }),
    [SoftTickerState.ResultSuccess]: /*#__PURE__*/React.createElement(UpdateResultSuccess, {
      match: match,
      tickerEvents: tickerEvents,
      onClose: () => handleClose('live_result', 'layer_report_result_success'),
      onResume: handleResultResumeToScorer
    }),
    [SoftTickerState.ScorerAdd]: /*#__PURE__*/React.createElement(UpdateScorer, {
      match: match,
      onClose: handleScorerClose,
      onUpdateScorer: handleUpdateScorer
    }),
    [SoftTickerState.ScorerUpdate]: /*#__PURE__*/React.createElement(UpdateScorer, {
      match: match,
      onClose: () => handleClose(),
      onUpdateScorer: handleUpdateScorer,
      onDelete: handleDeleteScorer
    })
  };
  const handleDialogClose = () => {
    var _closeEventByState$so, _closeEventByState$so2;
    const eventData = (_closeEventByState$so = (_closeEventByState$so2 = closeEventByState[softTickerData.state]) === null || _closeEventByState$so2 === void 0 ? void 0 : _closeEventByState$so2.call(closeEventByState, softTickerData.initiatorView)) !== null && _closeEventByState$so !== void 0 ? _closeEventByState$so : {};
    handleClose(eventData.event, eventData.view);
  };
  const height = useMemo(() => {
    switch (softTickerData.state) {
      case SoftTickerState.Result:
      case SoftTickerState.ResultSuccess:
        return 421;
      case SoftTickerState.ScorerAdd:
      case SoftTickerState.ScorerUpdate:
        return 580;
      default:
        return 0;
    }
  }, [softTickerData.state]);
  const allowSoftTicker = useAllowSoftTicker(match);
  const allowSoftTickerEvents = useAllowSoftTickerEvents(match);
  return allowSoftTicker || allowSoftTickerEvents ? /*#__PURE__*/React.createElement(CustomDialog, {
    TransitionComponent: Transition,
    onClose: handleDialogClose,
    open: softTickerData.open,
    height: height,
    "data-sentry-element": "CustomDialog",
    "data-sentry-component": "SoftTickerDialog",
    "data-sentry-source-file": "SoftTickerDialog.tsx"
  }, /*#__PURE__*/React.createElement(Header, {
    "data-sentry-element": "Header",
    "data-sentry-source-file": "SoftTickerDialog.tsx"
  }, /*#__PURE__*/React.createElement(IconButton, {
    color: "inherit",
    onClick: handleDialogClose,
    "aria-label": "close",
    "data-sentry-element": "IconButton",
    "data-sentry-source-file": "SoftTickerDialog.tsx"
  }, /*#__PURE__*/React.createElement(_Close, {
    color: "action",
    fontSize: "small",
    "data-sentry-element": "Close",
    "data-sentry-source-file": "SoftTickerDialog.tsx"
  })), /*#__PURE__*/React.createElement(Title, {
    variant: "title2",
    "data-sentry-element": "Title",
    "data-sentry-source-file": "SoftTickerDialog.tsx"
  }, dialogTitleByState[softTickerData.state])), /*#__PURE__*/React.createElement(Body, {
    "data-sentry-element": "Body",
    "data-sentry-source-file": "SoftTickerDialog.tsx"
  }, renderChild[softTickerData.state])) : null;
};
export default SoftTickerDialog;